import { Fragment, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DockPane } from './DockPane';
import { isGroup, minimumFraction, type DockNode, type DockSplitNode } from './dockModel';
import { useWorkspaceActions } from './WorkspaceProvider';

/** How much one arrow key moves a splitter, for a hand that is not on the pointer. */
const keyboardStep = 0.02;

/**
 * The dock tree, drawn.
 *
 * Splits become flex containers and their fractions become `flex-grow`, so the browser
 * does the arithmetic and a resized window redistributes the panes without a single
 * measurement in script. Only dragging a splitter needs to measure, and it measures the
 * two panes either side of it rather than the whole tree.
 */
export function DockLayout({ node }: { node: DockNode }) {
  if (isGroup(node)) return <DockPane group={node} />;
  return (
    <div className={`dock-split dock-split--${node.direction}`}>
      {node.children.map((child, index) => (
        <Fragment key={child.id}>
          {index === 0 ? null : <DockSplitter split={node} index={index - 1} />}
          <div className="dock-split__cell" style={{ flexGrow: node.sizes[index] ?? 1 / node.children.length }}>
            <DockLayout node={child} />
          </div>
        </Fragment>
      ))}
    </div>
  );
}

function DockSplitter({ split, index }: { split: DockSplitNode; index: number }) {
  const { t } = useTranslation();
  const { dispatch } = useWorkspaceActions();
  const handle = useRef<HTMLDivElement | null>(null);

  const before = split.sizes[index] ?? 0;
  const after = split.sizes[index + 1] ?? 0;
  const pair = before + after;
  const share = pair <= 0 ? 0.5 : before / pair;

  const applyFromPointer = (clientX: number, clientY: number) => {
    const element = handle.current;
    const previous = element?.previousElementSibling;
    const next = element?.nextElementSibling;
    if (!(previous instanceof HTMLElement) || !(next instanceof HTMLElement)) return;

    const first = previous.getBoundingClientRect();
    const second = next.getBoundingClientRect();
    if (split.direction === 'column') {
      const start = Math.min(first.top, second.top);
      const size = Math.max(first.height + second.height, 1);
      dispatch({ type: 'resize', splitId: split.id, index, fraction: ((clientY - start) / size) * pair });
      return;
    }
    // In Persian the first child is the pane on the right, so the distance that grows it
    // is measured from the far edge of the pair rather than the near one.
    const rtl = document.documentElement.dir === 'rtl';
    const start = Math.min(first.left, second.left);
    const end = Math.max(first.right, second.right);
    const size = Math.max(end - start, 1);
    const measured = rtl ? end - clientX : clientX - start;
    dispatch({ type: 'resize', splitId: split.id, index, fraction: (measured / size) * pair });
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.classList.add(split.direction === 'row' ? 'is-dock-resizing-x' : 'is-dock-resizing-y');
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    applyFromPointer(event.clientX, event.clientY);
  };

  const release = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    document.body.classList.remove('is-dock-resizing-x', 'is-dock-resizing-y');
  };

  return (
    <div
      ref={handle}
      className={`dock-splitter dock-splitter--${split.direction}`}
      role="separator"
      tabIndex={0}
      aria-orientation={split.direction === 'row' ? 'vertical' : 'horizontal'}
      aria-label={t('workspace.resizePanes')}
      aria-valuemin={Math.round(minimumFraction * 100)}
      aria-valuemax={Math.round((1 - minimumFraction) * 100)}
      aria-valuenow={Math.round(share * 100)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={release}
      onPointerCancel={release}
      onKeyDown={(event) => {
        const grow = split.direction === 'row'
          ? event.key === (document.documentElement.dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight')
          : event.key === 'ArrowDown';
        const shrink = split.direction === 'row'
          ? event.key === (document.documentElement.dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft')
          : event.key === 'ArrowUp';
        if (!grow && !shrink) return;
        event.preventDefault();
        dispatch({ type: 'resize', splitId: split.id, index, fraction: before + (grow ? keyboardStep : -keyboardStep) });
      }}
    >
      <span className="dock-splitter__grip" aria-hidden="true" />
    </div>
  );
}
